/**
 * PCM16 Downsampler AudioWorklet Code
 * Export the worklet code as a string for Blob URL creation
 */
const pcmWorkletCode = `
/**
 * PCM16Downsampler - AudioWorkletProcessor for real-time audio downsampling
 *
 * Purpose:
 * Converts high sample rate audio (e.g., 48kHz) to target sample rate PCM16LE format (e.g., 44.1kHz)
 * for efficient transmission over network or processing by speech recognition systems.
 *
 * Features:
 * - Linear interpolation downsampling to target sample rate
 * - Float32 to PCM16LE conversion (16-bit signed integer)
 * - Efficient buffer management with pre-allocation to minimize GC pressure
 * - Frame-based emission for consistent packet sizes
 *
 * Security Considerations:
 * - Input validation on targetRate and frameMs prevents invalid configurations
 * - Sample clamping prevents PCM16 overflow/underflow
 * - Buffer pre-allocation limits memory growth
 * - Null checks prevent crashes from missing audio data
 *
 * Performance Optimizations:
 * - Pre-allocated buffers (cache, int16Buffer, mergeBuffer) reduce GC pressure
 * - Bitwise operations for fast integer conversion
 * - Conditional buffer reuse vs allocation based on size
 *
 * Known Limitations:
 * - Uses simple linear interpolation (no anti-aliasing filter)
 * - May introduce aliasing artifacts when downsampling by large ratios
 * - Assumes mono audio input (only processes first channel)
 *
 * @class PCM16Downsampler
 * @extends AudioWorkletProcessor
 */
class PCM16Downsampler extends AudioWorkletProcessor {
    /**
     * Initializes the PCM16 downsampler with target sample rate and frame duration
     *
     * @param {Object} opts - Processor options
     * @param {Object} opts.processorOptions - Configuration options
     * @param {number} [opts.processorOptions.targetRate=44100] - Target sample rate in Hz (must be > 0)
     * @param {number} [opts.processorOptions.frameMs=20] - Frame duration in milliseconds (must be > 0)
     * @throws {Error} If targetRate or frameMs are invalid
     *
     * Security: Validates input parameters to prevent invalid configurations
     * Vulnerability: Does not validate sampleRate global (assumes Web Audio API provides valid value)
     */
    constructor(opts) {
        super();
        // BROWSER COMPATIBILITY: Safe property access instead of optional chaining
        const processorOptions = (opts && opts.processorOptions) || {};
        const targetRate =
            processorOptions.targetRate !== undefined ? processorOptions.targetRate : 44100;
        const frameMs = processorOptions.frameMs !== undefined ? processorOptions.frameMs : 20;

        // SECURITY: Validation to prevent invalid configurations
        if (targetRate <= 0 || frameMs <= 0) {
            throw new Error('Invalid targetRate or frameMs');
        }

        // POTENTIAL VULNERABILITY: sampleRate is a global from AudioWorkletGlobalScope
        // If sampleRate is 0 or undefined, this.ratio will be Infinity or NaN
        // MITIGATION: Validate sampleRate exists and is valid
        if (!sampleRate || sampleRate <= 0) {
            throw new Error('Invalid sampleRate from AudioWorkletGlobalScope');
        }

        this.targetRate = targetRate;
        this.samplesPerFrame = Math.round((targetRate * frameMs) / 1000);
        this.ratio = sampleRate / targetRate;

        // Constants for PCM16 conversion
        this.PCM16_MIN = -0x8000; // -32768: minimum value for signed 16-bit integer
        this.PCM16_MAX = 0x7fff; // 32767: maximum value for signed 16-bit integer

        // SECURITY: Maximum buffer size to prevent memory exhaustion
        // At 44.1kHz: 10000 samples = ~227ms of audio = ~20KB
        // This is a safety limit - normal operation should stay well below this
        this.MAX_BUFFER_SIZE = 10000;

        // PERFORMANCE: Pre-allocate buffers to avoid GC pressure during real-time processing
        this.cache = new Float32Array(0); // Holds leftover samples between frames
        this.int16Buffer = new Int16Array(this.samplesPerFrame); // Reusable PCM16 output buffer

        // PERFORMANCE: Pre-allocate merge buffer (max expected size)
        // Assumes 128 samples per quantum (standard Web Audio block size) at source sample rate
        // SECURITY: Buffer size is bounded to prevent memory exhaustion
        this.maxMergeSize = Math.ceil(128 / this.ratio) + this.samplesPerFrame * 2;
        this.mergeBuffer = new Float32Array(this.maxMergeSize);

        // OPTIMIZATION: Pre-allocate downsampling buffer (max expected size for one quantum)
        this.maxDownsampleSize = Math.ceil(128 / this.ratio) + 1;
        this.downsampleBuffer = new Float32Array(this.maxDownsampleSize);
    }

    /**
     * Downsample audio from source sample rate to target sample rate using linear interpolation
     *
     * @param {Float32Array} inputFloat32 - Input audio samples at source sample rate
     * @returns {Float32Array} Downsampled audio at target sample rate
     *
     * Algorithm: Linear interpolation between adjacent samples
     * - Fast and efficient for real-time processing
     * - Trade-off: No anti-aliasing filter (may introduce artifacts with high-frequency content)
     *
     * OPTIMIZATION: Uses pre-allocated buffer when possible to reduce GC pressure
     * Falls back to new allocation if input is larger than expected
     *
     * @private
     */
    down(inputFloat32) {
        // Fast path: no downsampling needed
        if (this.ratio === 1) {
            return inputFloat32;
        }

        const outputLength = Math.floor(inputFloat32.length / this.ratio);

        // OPTIMIZATION: Use pre-allocated buffer when possible
        let output;
        if (outputLength <= this.downsampleBuffer.length) {
            output = this.downsampleBuffer;
        } else {
            // Fallback: allocate new buffer for unusually large input
            output = new Float32Array(outputLength);
        }

        let position = 0;

        for (let i = 0; i < outputLength; i++) {
            // PERFORMANCE: Bitwise OR trick for fast floor operation (position | 0)
            const index0 = position | 0;
            // SECURITY: Bounds check to prevent out-of-bounds read
            const index1 = Math.min(index0 + 1, inputFloat32.length - 1);
            const fraction = position - index0;

            // Linear interpolation between adjacent samples
            // Formula: y = y0 * (1 - t) + y1 * t, where t is fraction between samples
            output[i] = inputFloat32[index0] * (1 - fraction) + inputFloat32[index1] * fraction;
            position += this.ratio;
        }

        // Return view of correct length (important when using pre-allocated buffer)
        return output.subarray(0, outputLength);
    }

    /**
     * Convert Float32 samples to PCM16LE and emit frames via postMessage
     *
     * @param {Float32Array} allSamples - Downsampled audio samples to process
     * @returns {Float32Array} Remaining samples that didn't fill a complete frame
     *
     * Process:
     * 1. Splits input into fixed-size frames (samplesPerFrame)
     * 2. Converts Float32 (-1.0 to 1.0) to PCM16 (-32768 to 32767)
     * 3. Sends each frame to main thread via postMessage
     * 4. Returns leftover samples for next processing cycle
     *
     * Message Format:
     * - Audio data: ArrayBuffer (raw PCM16 data)
     * - Errors: { type: 'error', message: string, ...details }
     *
     * SECURITY: Clamps samples to [-1, 1] range to prevent PCM16 overflow
     * PERFORMANCE: Reuses int16Buffer to avoid allocations
     *
     * @private
     */

    emit(allSamples) {
        let offset = 0;

        // Process complete frames
        while (offset + this.samplesPerFrame <= allSamples.length) {
            // Convert Float32 (-1.0 to 1.0) to PCM16 (-32768 to 32767)
            for (let i = 0; i < this.samplesPerFrame; i++) {
                // SECURITY: Clamp to prevent overflow/underflow
                const sample = Math.max(-1, Math.min(1, allSamples[offset + i]));

                // Convert to 16-bit signed integer
                // Negative samples: multiply by 32768 (abs of PCM16_MIN)
                // Positive samples: multiply by 32767 (PCM16_MAX)
                this.int16Buffer[i] =
                    sample < 0 ? sample * -this.PCM16_MIN : sample * this.PCM16_MAX;
            }

            // PERFORMANCE: slice(0) creates a copy for safe transfer via postMessage
            // OPTIMIZATION OPPORTUNITY: Could use Transferable objects to avoid copy
            // but would require coordination with receiving end
            this.port.postMessage(this.int16Buffer.buffer.slice(0));
            offset += this.samplesPerFrame;
        }

        // Return remaining samples that didn't fill a complete frame
        // These will be cached for the next processing cycle
        return allSamples.subarray(offset);
    }

    /**
     * Main audio processing callback (called by Web Audio API)
     *
     * @param {Float32Array[][]} inputs - 2D array of input audio channels
     *   inputs[0] = first input node
     *   inputs[0][0] = first channel of first input (mono channel we process)
     * @returns {boolean} true to keep processor alive, false to terminate
     *
     * Processing Pipeline:
     * 1. Extract mono channel from input
     * 2. Downsample to target sample rate
     * 3. Merge with cached samples from previous cycle
     * 4. Emit complete frames as PCM16
     * 5. Cache remaining incomplete frame
     *
     * SECURITY: Null checks prevent crashes from missing/empty input
     * PERFORMANCE: Pre-allocated mergeBuffer avoids GC, with fallback for edge cases
     *
     * POTENTIAL VULNERABILITY: If cache grows indefinitely without being emitted,
     * could cause memory exhaustion (mitigated by frame emission logic)
     */

    process(inputs) {
        // BROWSER COMPATIBILITY: Safe access to prevent crashes
        const input = inputs[0];
        const channel0 = input && input[0];

        // No audio data or empty buffer - keep processor alive for next cycle
        if (!channel0 || !channel0.length) {
            return true; // Return true to keep processor alive
        }

        // Step 1: Downsample from source to target sample rate
        const downsampled = this.down(channel0);

        // Step 2: Merge with cached samples from previous cycle
        const totalLength = this.cache.length + downsampled.length;

        // SECURITY: Hard limit to prevent memory exhaustion
        if (totalLength > this.MAX_BUFFER_SIZE) {
            // Send warning to main thread for monitoring/debugging
            this.port.postMessage({
                type: 'error',
                message: 'Buffer overflow detected',
                cacheSize: this.cache.length,
                downsampledSize: downsampled.length,
                totalSize: totalLength,
                maxSize: this.MAX_BUFFER_SIZE,
            });

            // IMPROVED STRATEGY: Emit what we have, then keep only recent data
            // This minimizes audio loss while preventing memory exhaustion

            // Try to emit the cache first
            if (this.cache.length > 0) {
                this.cache = this.emit(this.cache);
            }

            // If still too large, keep only the most recent data
            if (this.cache.length + downsampled.length > this.MAX_BUFFER_SIZE) {
                // Calculate how much we can keep
                const keepSize = Math.min(
                    this.samplesPerFrame,
                    this.MAX_BUFFER_SIZE - downsampled.length,
                );
                if (keepSize > 0 && this.cache.length > keepSize) {
                    // Keep only the tail of the cache
                    this.cache = this.cache.subarray(this.cache.length - keepSize);
                } else {
                    // Drop entire cache if necessary
                    this.cache = new Float32Array(0);
                }
            }

            // Merge remaining cache with new data
            const newTotalLength = this.cache.length + downsampled.length;
            if (newTotalLength <= this.mergeBuffer.length) {
                this.mergeBuffer.set(this.cache, 0);
                this.mergeBuffer.set(downsampled, this.cache.length);
                this.cache = this.emit(this.mergeBuffer.subarray(0, newTotalLength));
            } else {
                // Very edge case: just process new data
                this.cache = this.emit(downsampled);
            }

            return true;
        }

        // PERFORMANCE: Try to use pre-allocated buffer to avoid GC
        if (totalLength <= this.mergeBuffer.length) {
            // Fast path: use pre-allocated mergeBuffer
            this.mergeBuffer.set(this.cache, 0);
            this.mergeBuffer.set(downsampled, this.cache.length);
            this.cache = this.emit(this.mergeBuffer.subarray(0, totalLength));
        } else {
            // SECURITY: Fallback for buffers larger than pre-allocated size
            // This path is now safe due to MAX_BUFFER_SIZE check above
            const buffer = new Float32Array(totalLength);
            buffer.set(this.cache, 0);
            buffer.set(downsampled, this.cache.length);
            this.cache = this.emit(buffer);
        }

        // Return true to keep processor alive
        return true;
    }
}

// Register the processor with Web Audio API
// Name: "pcm16-downsampler" - use this name when creating AudioWorkletNode
registerProcessor('pcm16-downsampler', PCM16Downsampler);
`;

export default pcmWorkletCode;
